import React, { ForwardedRef, useState, useEffect, useRef } from 'react';
import {
  LineChart,
  LineChartOptions,
  SlopeChart,
  SlopeChartOptions,
  StackedBarChart,
  StackedBarChartOptions,
} from '../../design/charts';
import { Legend } from '../../design/charts/legend';
import { Loading } from '../../design/loading/Loading';
import { ChartTitle } from '../../design/charts/chartTitle';
import { ChartData } from '../data/common';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { DataWarning } from '../../types';
import { EmptyDataWarning } from '../../design/warning/emptyDataWarning';
import { WarningsDisplay } from '../../design/warning/warningsDisplay';
import { Formatting } from '../../data/formatting';
import { DataErrorElement } from '../error/error';
import { DEFAULT_ERROR_MESSAGE } from '../../common/constants/error';
import classNames from 'classnames';
import { useMobile } from '../../context/mobile/mobile.provider';
import { CHART_TITLE_HEIGHT } from '../../design/charts/constants';
import { useScrollbarHeight } from '../../design/charts/useScrollbarHeight.hook';
import { getChartHeight } from '../../design/charts/utils';

type Props = {
  data: ChartData[];
  currency: string;
  trackTooltipEvent: () => void;
  isLoading?: boolean;
  isEnrichmentLoading?: boolean;
  enrichmentError?: string;
  chartFullscreen?: boolean;
};
const MultiKpiChart = React.forwardRef<HTMLDivElement, Props>(
  (
    { data, currency, trackTooltipEvent, isLoading, isEnrichmentLoading, enrichmentError, chartFullscreen }: Props,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const includeTotals = data[0]?.totalsDataSet !== undefined;
    let totalsLabel = includeTotals ? 'Market totals' : undefined;
    let totalsChangeLabel = includeTotals ? 'Change' : undefined;
    let isEmptyData = false;
    const { isMobile } = useMobile();
    const [isLandscape, setIsLandscape] = useState(
      typeof window !== 'undefined' && window.innerWidth > window.innerHeight
    );
    const loading = isLoading || isEnrichmentLoading;
    const chartOverflowContainerRef = useRef<HTMLDivElement>(null);
    const scrollbarHeight = useScrollbarHeight(chartOverflowContainerRef);
    const isChartFullView = isLandscape || chartFullscreen;
    const chartHeightOffset = isMobile ? CHART_TITLE_HEIGHT + scrollbarHeight + 28 : 0; // mt-7 margin is equal to 28px

    useEffect(() => {
      const handleOrientationChange = () => {
        setIsLandscape(window.innerWidth > window.innerHeight);
      };

      window.addEventListener('resize', handleOrientationChange);
      window.addEventListener('orientationchange', handleOrientationChange);

      return () => {
        window.removeEventListener('resize', handleOrientationChange);
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }, []);

    if (isEmptyData) {
      return <EmptyState height="500px" />;
    }

    return (
      <div ref={ref} className="relative w-full flex overflow-x-auto">
        <div
          className={classNames('w-full flex flex-rows items-stretch gap-2', {
            'flex-column-reverse mt-7': isMobile,
          })}
        >
          <Legend
            css="pt-10"
            data={data.find(item => !!item.dataSets)?.dataSets}
            totalsLabel={totalsLabel}
            totalsChangeLabel={totalsChangeLabel}
          />
          <div className="relative flex-grow flex flex-row items-stretch overflow-x-auto">
            {data.map((set, idx) => (
              <KpiChart
                isLoading={loading}
                key={`${set?.yColumn.label}_${idx}`}
                data={set}
                currency={currency}
                trackTooltipEvent={trackTooltipEvent}
                enrichmentError={enrichmentError}
                isChartFullView={isChartFullView}
                chartHeightOffset={chartHeightOffset}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

type KpiChartProps = {
  data: ChartData;
  currency: string;
  isLoading: boolean;
  trackTooltipEvent: () => void;
  enrichmentError: string;
  isChartFullView: boolean;
  chartHeightOffset: number;
};

function KpiChart({
  data,
  currency,
  trackTooltipEvent,
  isLoading,
  enrichmentError,
  isChartFullView,
  chartHeightOffset,
}: Readonly<KpiChartProps>) {
  const { yColumn, dataSets, totalsDataSet, dataLabels } = data;
  let Chart: JSX.Element | null = null;
  const isSeries = data.dataLabels?.length > 2;
  const { isMobile } = useMobile();

  const formatTooltipValue = (value: string | number): string => {
    return Formatting.format(Number(value), {
      dataType: yColumn.dataType,
      formatHint: yColumn.formatHint,
      currency,
    });
  };

  if (yColumn?.canAggregate) {
    const chartOptions: StackedBarChartOptions = {
      yScale: {
        stacked: true,
      },
      data: {
        sets: dataSets,
        summary: totalsDataSet,
        labels: dataLabels,
      },
      tooltip: {
        valueFormatter: value => formatTooltipValue(value),
      },
      changesOnLastStack: !isSeries,
    };
    Chart = (
      <StackedBarChart
        options={chartOptions}
        trackTooltipEvent={trackTooltipEvent}
        isMobile={isMobile}
        isChartFullView={isChartFullView}
        chartHeightOffset={chartHeightOffset}
      />
    );
  } else {
    if (isSeries) {
      const lineChartOptions: LineChartOptions = {
        yScale: {
          formatter: value => formatTooltipValue(value),
        },
        data: {
          sets: dataSets,
          summary: totalsDataSet,
          labels: dataLabels,
        },
        tooltip: {
          valueFormatter: value => formatTooltipValue(value),
        },
      };
      Chart = (
        <LineChart
          options={lineChartOptions}
          trackTooltipEvent={trackTooltipEvent}
          isMobile={isMobile}
          isChartFullView={isChartFullView}
          chartHeightOffset={chartHeightOffset}
        />
      );
    } else {
      const slopeChartOptions: SlopeChartOptions = {
        data: {
          sets: dataSets,
          summary: totalsDataSet,
          labels: dataLabels,
        },
        tooltip: {
          valueFormatter: value => formatTooltipValue(value),
        },
      };
      Chart = (
        <SlopeChart
          options={slopeChartOptions}
          trackTooltipEvent={trackTooltipEvent}
          isMobile={isMobile}
          isChartFullView={isChartFullView}
          chartHeightOffset={chartHeightOffset}
        />
      );
    }
  }

  return (
    <div className="relative px-4 flex-column h-full justify-between">
      {!data?.hasData && <Loading isActive={isLoading} withIndicator />}
      <ChartTitle title={yColumn.label} />
      <ChartWarnings
        isLoading={isLoading}
        hasData={data?.hasData}
        warnings={data?.warnings}
        enrichmentError={enrichmentError}
      />
      {data?.hasData && (
        <div style={{ height: getChartHeight(isMobile, isChartFullView, false, chartHeightOffset) }}>{Chart}</div>
      )}
    </div>
  );
}

export const ChartWarnings: React.FC<{
  warnings?: DataWarning[];
  hasData?: boolean;
  isLoading: boolean;
  enrichmentError: string;
}> = ({ hasData, warnings, isLoading, enrichmentError }) => {
  if (hasData || isLoading) {
    return null;
  }
  if (enrichmentError) {
    const errorInfo = {
      message: DEFAULT_ERROR_MESSAGE,
      className: 'pt-10',
      title: 'Unable to get data.',
    };
    return <DataErrorElement error={errorInfo} />;
  }

  if (warnings?.length > 0) {
    return <WarningsDisplay warnings={warnings} className="pt-15" style={{ maxWidth: 300 }} />;
  }

  return <EmptyDataWarning style={{ maxWidth: 300 }} />;
};

export { MultiKpiChart };
